// Firebase web SDK setup — hosting/analytics/notifications/remote config.
// Config comes from REACT_APP_FIREBASE_* env vars (project: tossit-uat, matches backend firebase-admin creds).
import { initializeApp } from 'firebase/app';
import { getAnalytics, isSupported as analyticsIsSupported } from 'firebase/analytics';
import { getMessaging, getToken, onMessage, isSupported as messagingIsSupported } from 'firebase/messaging';
import { getRemoteConfig } from 'firebase/remote-config';

export const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

export const app = initializeApp(firebaseConfig);

// Analytics — only in a supported browser environment. `analytics` is null until the
// support check resolves; `analyticsReady` resolves with the instance (or null) so the
// analytics service can queue events until it's ready.
export let analytics = null;
export const analyticsReady = (typeof window !== 'undefined')
  ? analyticsIsSupported()
      .then((ok) => { if (ok) analytics = getAnalytics(app); return analytics; })
      .catch(() => null)
  : Promise.resolve(null);

// Messaging (FCM web) — returns the messaging instance if supported, else null
export const getMessagingIfSupported = async () => {
  try {
    if (typeof window === 'undefined') return null;
    if (!(await messagingIsSupported())) return null;
    return getMessaging(app);
  } catch {
    return null;
  }
};

// Request notification permission + FCM web token. Defaults to the env VAPID key
// (Firebase console → Project settings → Cloud Messaging → Web Push certificates).
export const requestFcmWebToken = async (vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY) => {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return null;
  if (!vapidKey) return null;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;
  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  return getToken(messaging, { vapidKey, serviceWorkerRegistration: registration });
};

// Foreground push listener
export const onForegroundMessage = async (cb) => {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return () => {};
  return onMessage(messaging, cb);
};

// Remote Config — feature flags. `featureFlags` is a JSON string param; the app
// reads it via hooks/useFeatureFlags. Default keeps everything off until the
// Firebase console (project tossit-uat) provides values.
export const remoteConfig = typeof window !== 'undefined' ? getRemoteConfig(app) : null;
if (remoteConfig) {
  remoteConfig.settings.minimumFetchIntervalMillis = 3600000; // 1 hour
  remoteConfig.defaultConfig = {
    featureFlags: JSON.stringify({ referral: false, multiBooking: false }),
  };
}
